/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * FormDataTab — where a form's answers get saved.
 *
 * One tab holds the whole map: which table each answer lands in, which columns
 * the server fills in by itself, and whether a copy of the submission is kept.
 * Deliberately NOT spread across each field's settings panel — the useful thing
 * about a form's wiring is seeing all of it at once, including the answers that
 * are not being saved anywhere.
 *
 * WHAT IT WRITES. The `operations` list, in exactly the shape the backend and
 * the action buttons already use:
 *
 *   [{ ref, table, visitor_fields: {column: '@form.<field id>'},
 *      server_fields: {column: {from: 'user_id'}} }]
 *
 * Note the inversion: this panel reads "field → column" because that is how a
 * person thinks about a form, while the stored shape is "column → answer"
 * because that is how a row is written. The mapping is turned around here so
 * neither side has to compromise.
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHelper } from '../../../hooks/usePages';
import { useTheme } from '../../../theme/ThemeContext';

// ─── Reading the form's own fields ───────────────────────────────────────────

/** Every field on the form, whichever layout it uses. */
export function allFormFields(cfg) {
  const out = [];
  const fromRows = rows => (rows || []).forEach(r => (r.fields || []).forEach(f => out.push(f)));

  if (cfg.stagesEnabled) (cfg.stages || []).forEach(s => fromRows(s.rows));
  else if (cfg.rowLayoutEnabled) fromRows(cfg.rows);
  else (cfg.fields || []).forEach(f => out.push(f));

  return out;
}

/** `Full name` → `full_name`, so an automatic match has something to match on. */
function slugify(text) {
  return String(text || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Columns this field plausibly belongs in, best first. Used only to pre-fill —
 * every guess is visible in the list and changeable.
 */
export function guessColumn(field, columns) {
  const names = new Set(columns.map(c => c.name));
  for (const candidate of [field.id, slugify(field.label)]) {
    if (candidate && names.has(candidate)) return candidate;
  }
  return '';
}

// ─── The field ⇄ column inversion ────────────────────────────────────────────
//
// Stored as column → answer, because that is how a row is written. Shown as
// field → column, because that is how a person reads a form. Both directions
// live here so the panel never has to think about it.

/** {column: '@form.x'} → {x: column}. Anything not a form answer is ignored. */
export function columnByField(operation) {
  const map = {};
  Object.entries((operation || {}).visitor_fields || {}).forEach(([column, source]) => {
    if (typeof source === 'string' && source.startsWith('@form.')) {
      map[source.slice('@form.'.length)] = column;
    }
  });
  return map;
}

/**
 * Points one field at one column, returning a new operation.
 *
 * A column holds at most one answer, and an answer lands in at most one column,
 * so assigning either end releases whatever was there before. Choosing the
 * empty column unmaps the field — its answer is then simply not saved.
 */
export function withFieldColumn(operation, fieldId, column) {
  const current = columnByField(operation);
  const visitor_fields = {};
  Object.entries(current).forEach(([fid, col]) => {
    if (fid !== fieldId && col && col !== column) visitor_fields[col] = `@form.${fid}`;
  });
  if (column) visitor_fields[column] = `@form.${fieldId}`;
  return { ...operation, visitor_fields };
}

// ─── Rules carried over from the bound column ────────────────────────────────
//
// The platform already knows how long a value may be and which characters it
// accepts — `max_length`, `allowed_characters` and `validation_rules` have been
// on every column all along, and until now nothing read them, so each form that
// wanted a limit re-stated it in its own code.
//
// Mapping a field to a column copies those rules onto the field, so the form
// carries them to the browser and both ends agree without anybody typing a
// limit twice. Only the RULES travel — never the table or column name. The
// browser is deliberately never told what it is writing into.

/**
 * The rules a column imposes, or undefined when it imposes none.
 *
 * `validation_rules` wins over the column's own `max_length`: an unbounded
 * `text` column with a declared limit is exactly the case the column length
 * cannot express.
 */
export function rulesFromColumn(column) {
  if (!column) return undefined;
  const declared = column.validation_rules || {};
  const rules = {};

  const maxLength = declared.max_length ?? column.max_length;
  if (maxLength) rules.maxLength = maxLength;
  if (declared.min_length) rules.minLength = declared.min_length;

  const pattern = declared.pattern || column.allowed_characters;
  if (pattern) {
    rules.pattern = pattern;
    if (declared.pattern_message) rules.patternMessage = declared.pattern_message;
  }

  const dataType = String(column.data_type || '').toLowerCase();
  if (dataType) rules.dataType = dataType;

  return Object.keys(rules).length ? rules : undefined;
}

/** Rewrites every field on the form, whichever layout it uses. */
export function mapFormFields(cfg, fn) {
  const mapRows = rows => (rows || []).map(r => ({ ...r, fields: (r.fields || []).map(fn) }));

  if (cfg.stagesEnabled) return { stages: (cfg.stages || []).map(s => ({ ...s, rows: mapRows(s.rows) })) };
  if (cfg.rowLayoutEnabled) return { rows: mapRows(cfg.rows) };
  return { fields: (cfg.fields || []).map(fn) };
}

function fieldWithRules(field, rules) {
  if (rules) return { ...field, rules };
  if (!field.rules) return field;
  const { rules: _dropped, ...rest } = field;
  return rest;
}

/**
 * The config patch that keeps field rules in step with a mapping change.
 *
 * Only fields whose column actually changed in THIS operation are touched, so
 * a second operation's mapping is never disturbed. Returns {} when there is
 * nothing to do — including when the columns have not loaded yet, since
 * guessing at rules from an empty column list would quietly erase real ones.
 */
export function fieldRulePatch(cfg, prevOp, nextOp, columns) {
  if (!Array.isArray(columns) || columns.length === 0) return {};

  const before = columnByField(prevOp);
  const after = columnByField(nextOp);
  const byName = new Map(columns.map(c => [c.name, c]));

  const changed = {};
  new Set([...Object.keys(before), ...Object.keys(after)]).forEach(fieldId => {
    if (before[fieldId] === after[fieldId]) return;
    changed[fieldId] = after[fieldId] ? rulesFromColumn(byName.get(after[fieldId])) : undefined;
  });

  if (Object.keys(changed).length === 0) return {};
  return mapFormFields(cfg, f => (
    Object.prototype.hasOwnProperty.call(changed, f.id) ? fieldWithRules(f, changed[f.id]) : f
  ));
}

/** Every field mapped by name that has a matching column and is still free. */
export function autoMapped(operation, formFields, columns) {
  const visitor_fields = {};
  formFields.forEach(field => {
    const column = guessColumn(field, columns);
    if (column && !visitor_fields[column]) visitor_fields[column] = `@form.${field.id}`;
  });
  return { ...operation, visitor_fields };
}

// ─── Server-filled values ────────────────────────────────────────────────────

const SERVER_RULES = [
  { key: 'user_id', label: 'The signed-in person', rule: { from: 'user_id' } },
  { key: 'now',     label: 'The time it was submitted', rule: { from: 'now' } },
  { key: 'const',   label: 'A fixed value', rule: { const: '' } },
];

function describeRule(rule) {
  if (!rule || typeof rule !== 'object') return '';
  if ('const' in rule) return 'const';
  if (typeof rule.from === 'string' && rule.from.startsWith('@')) return 'ref';
  return rule.from || '';
}

/** The column on this operation filled with the signed-in person, or ''. */
export function ownerColumnOf(operation) {
  const entries = Object.entries((operation || {}).server_fields || {});
  const found = entries.find(([, rule]) => rule && rule.from === 'user_id');
  return found ? found[0] : '';
}

// ─── Small shared bits ───────────────────────────────────────────────────────

const labelStyle = { fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 };
const inputStyle = {
  width: '100%', padding: '5px 8px', fontSize: 11,
  border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', background: '#fff',
};

function SectionLabel({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 8px' }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: '#94a3b8',
        letterSpacing: '0.08em', whiteSpace: 'nowrap',
      }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
      {right}
    </div>
  );
}

function Empty({ icon, children }) {
  return (
    <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '22px 8px' }}>
      <i className={`bi ${icon}`} style={{ fontSize: 18, display: 'block', marginBottom: 6, color: '#cbd5e1' }} />
      {children}
    </div>
  );
}

// ─── Creating an account ─────────────────────────────────────────────────────

export const IDENTITY_KIND = 'app_user';

export function isIdentityOperation(operation) {
  return (operation || {}).kind === IDENTITY_KIND;
}

/** The account details registration accepts, and what each is for. */
const IDENTITY_FIELDS = [
  { name: 'email',      label: 'Email',      required: true,
    hint: 'Their login handle. One account per email.' },
  { name: 'password',   label: 'Password',   required: true,
    hint: 'Checked against the platform’s password rules.' },
  { name: 'first_name', label: 'First name', required: false, hint: '' },
  { name: 'last_name',  label: 'Last name',  required: false, hint: '' },
  { name: 'username',   label: 'Username',   required: false,
    hint: 'Optional. Must be unique within this application.' },
];

function IdentityCard({ op, index, total, formFields, roles, loadingRoles,
  groups, loadingGroups, theme, onChange, onRemove }) {
  const mode = op.mode || 'register';
  const mapped = op.fields || {};

  const setMode = (next) => onChange(index, { ...op, mode: next });

  /**
   * The role is written as a CONSTANT — `{const: '<name>'}`, never `@form.…`.
   * The person filling in the form must not be able to choose what they become;
   * only the builder decides. The server enforces this too and refuses anything
   * else, so this control cannot express the unsafe shape in the first place.
   */
  const chosenRole = (op.assign_role || {}).const || '';

  const setRole = (name) => {
    const next = { ...op };
    if (name) next.assign_role = { const: name };
    else delete next.assign_role;
    onChange(index, next);
  };

  /**
   * The group is written the same way and for the same reason — a constant the
   * builder picks, never `@form.…`. A scope read from the form would let the
   * person signing up widen themselves from their own records to everybody's.
   *
   * GLOBAL is not offered. It means "every row in the application", which is a
   * grant an administrator makes deliberately, not something a public signup
   * form hands to whoever fills it in. The server refuses it too.
   */
  const chosenGroup = (op.assign_group || {}).const || '';
  const chosenScope = (op.assign_group || {}).rls_scope || 'SELF';

  const setGroup = (name) => {
    const next = { ...op };
    if (name) next.assign_group = { const: name, rls_scope: chosenScope };
    else delete next.assign_group;
    onChange(index, next);
  };

  const setScope = (scope) => {
    if (!chosenGroup) return;
    onChange(index, { ...op, assign_group: { const: chosenGroup, rls_scope: scope } });
  };

  /**
   * `send_email` absent means no mail — the behaviour every form had before the
   * flag existed. A legacy bare `true` is shown as Verification, which is what
   * the server reads it as: mailing somebody a password they never chose, to an
   * address nobody has proved they own, must never be what an unqualified yes
   * quietly means.
   */
  const chosenMail = op.send_email === true ? 'verification' : (op.send_email || '');

  const setMail = (mode) => {
    const next = { ...op };
    if (mode) next.send_email = mode;
    else delete next.send_email;
    onChange(index, next);
  };

  const setField = (name, fieldId) => {
    const next = { ...mapped };
    if (fieldId) next[name] = `@form.${fieldId}`;
    else delete next[name];
    onChange(index, { ...op, fields: next });
  };

  const fieldIdFor = (name) => {
    const v = mapped[name];
    return (typeof v === 'string' && v.startsWith('@form.')) ? v.slice('@form.'.length) : '';
  };

  const taken = new Set(Object.values(mapped)
    .filter(v => typeof v === 'string' && v.startsWith('@form.'))
    .map(v => v.slice('@form.'.length)));

  return (
    <div style={{
      border: '1px solid #ddd6fe', borderRadius: 8, padding: 12,
      background: '#faf5ff', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ flex: 1 }}>
          {/*
            ALWAYS "first", wherever this card sits in the list. The server
            partitions identity operations out and runs them ahead of every
            table write, so position here decides nothing about order — and a
            card reading "Then, the person" while sitting below a table was
            simply untrue, and alarming to anybody who noticed.
          */}
          <div style={labelStyle}>
            <i className="bi bi-person-plus me-1" />
            First, the person
          </div>
          {/*
            Said HERE, not only in the summary above the list. This card is
            where the doubt arises — it can sit below a table card, and the
            reassurance is worth nothing if the reader has to scroll away from
            the thing that worried them to find it.
          */}
          {index > 0 && (
            <div style={{
              fontSize: 10, color: '#7e22ce', lineHeight: 1.5,
              marginTop: 3, marginBottom: 5,
            }}>
              <i className="bi bi-info-circle me-1" />
              Runs before the table{index > 1 ? 's' : ''} listed above, even though
              this card sits below. Order here does not change when things happen.
            </div>
          )}
          <select style={inputStyle} value={mode} onChange={e => setMode(e.target.value)}>
            <option value="register">Create an account for them</option>
            <option value="current">Use the person already signed in</option>
          </select>
        </div>
        <button type="button" onClick={() => onRemove(index)} title="Remove"
          style={{
            border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626',
            borderRadius: 6, padding: '5px 8px', fontSize: 11, cursor: 'pointer',
          }}>
          <i className="bi bi-trash" />
        </button>
      </div>

      {mode === 'register' ? (
        <>
          <SectionLabel>ACCOUNT DETAILS</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {IDENTITY_FIELDS.map(f => {
              const chosen = fieldIdFor(f.name);
              return (
                <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: '38%', fontSize: 11,
                    color: (!chosen && f.required) ? '#b45309' : '#374151',
                  }} title={f.hint}>
                    {(!chosen && f.required) && (
                      <i className="bi bi-exclamation-triangle-fill me-1"
                        style={{ fontSize: 9, color: '#f59e0b' }} />
                    )}
                    {f.label}{f.required ? ' *' : ''}
                  </span>
                  <i className="bi bi-arrow-left" style={{ fontSize: 9, color: '#cbd5e1' }} />
                  <select style={{ ...inputStyle, flex: 1 }} value={chosen}
                    onChange={e => setField(f.name, e.target.value)}>
                    <option value="">— not asked —</option>
                    {formFields.map(ff => (
                      <option key={ff.id} value={ff.id}
                        disabled={taken.has(ff.id) && ff.id !== chosen}>
                        {ff.label || ff.id}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <SectionLabel>WHAT THEY BECOME</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: '38%', fontSize: 11, color: '#374151' }}
              title="Every account this form creates is given this role.">
              Role
            </span>
            <i className="bi bi-arrow-left" style={{ fontSize: 9, color: '#cbd5e1' }} />
            <select style={{ ...inputStyle, flex: 1 }} value={chosenRole}
              disabled={loadingRoles}
              onChange={e => setRole(e.target.value)}>
              <option value="">— no role —</option>
              {(roles || []).map(r => (
                <option key={r.id} value={r.name}>{r.label || r.name}</option>
              ))}
            </select>
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.5, marginTop: 5 }}>
            {loadingRoles
              ? 'Loading this application’s roles…'
              : (roles || []).length === 0
                ? 'This application has no roles yet. Create one under Roles and it appears here.'
                : 'Everyone who completes this form is given this role. The list follows the application’s roles — add one and it shows up here.'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ width: '38%', fontSize: 11, color: '#374151' }}
              title="Which group the account is placed in. This decides which ROWS it can see; the role above decides which tables.">
              Group
            </span>
            <i className="bi bi-arrow-left" style={{ fontSize: 9, color: '#cbd5e1' }} />
            <select style={{ ...inputStyle, flex: 1 }} value={chosenGroup}
              disabled={loadingGroups}
              onChange={e => setGroup(e.target.value)}>
              <option value="">— no group —</option>
              {(groups || []).map(g => (
                <option key={g.id} value={g.name}>{g.name}</option>
              ))}
            </select>
          </div>

          {chosenGroup ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span style={{ width: '38%', fontSize: 11, color: '#374151' }}
                title="How much of the group this account can see.">
                Can see
              </span>
              <i className="bi bi-arrow-left" style={{ fontSize: 9, color: '#cbd5e1' }} />
              <select style={{ ...inputStyle, flex: 1 }} value={chosenScope}
                onChange={e => setScope(e.target.value)}>
                <option value="SELF">Only their own records</option>
                <option value="GROUP">Everything in their group</option>
                <option value="HIERARCHICAL">Their group and everything under it</option>
              </select>
            </div>
          ) : null}

          <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.5, marginTop: 5 }}>
            {loadingGroups
              ? 'Loading this application’s groups…'
              : (groups || []).length === 0
                ? 'This application has no groups yet. Create one under Groups and it appears here.'
                : chosenGroup
                  ? 'The role above decides which tables they can use. This decides which rows of them they can see.'
                  : 'Leave this empty only if everyone who signs up should read every row. On a table whose visibility is Public, an account with no group is widened to see ALL records — so an account meant to see only its own needs a group here.'}
          </div>

          <SectionLabel>WHAT THEY ARE SENT</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: '38%', fontSize: 11, color: '#374151' }}
              title="Sent only once the whole submission has succeeded.">
              Email
            </span>
            <i className="bi bi-arrow-left" style={{ fontSize: 9, color: '#cbd5e1' }} />
            <select style={{ ...inputStyle, flex: 1 }} value={chosenMail}
              onChange={e => setMail(e.target.value)}>
              <option value="">— no email —</option>
              <option value="verification">Verification link</option>
              <option value="credentials">Their password</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.5, marginTop: 5 }}>
            {chosenMail === 'credentials' || chosenMail === 'both'
              ? 'Sends the password they typed. Only appropriate when somebody else is creating the account for them — never on a public signup.'
              : chosenMail === 'verification'
                ? 'They prove the address is theirs before they can sign in.'
                : 'Nothing is sent. The account is usable but its address stays unverified.'}
          </div>

          <div style={{
            marginTop: 10, padding: '7px 9px', background: '#fffbeb',
            borderRadius: 6, border: '1px solid #fde68a', fontSize: 10,
            color: '#92400e', lineHeight: 1.5,
          }}>
            <i className="bi bi-info-circle me-1" />
            A form that creates an account can be filled in by somebody who does not
            have one yet — the sign-in rule would otherwise make signing up impossible.
            Email is sent only after the whole submission succeeds, so a refused
            submission never mails anybody about an account it did not create.
          </div>
        </>
      ) : (
        <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.5, marginTop: 8 }}>
          Uses whoever is signed in. The form asks for nothing here, and refuses the
          submission if nobody is signed in.
        </div>
      )}
    </div>
  );
}

// ─── One table's mapping ─────────────────────────────────────────────────────

function OperationCard({
  op, index, total, tableStep, tables, formFields, accountFields, appId, theme,
  earlierRefs, onChange, onRemove,
}) {
  const table = tables.find(t => t.name === op.table);

  const { data: details } = useQuery({
    queryKey: ['tableColumns', appId, table?.id],
    queryFn: async ({ signal }) => {
      // POST, not GET: this endpoint takes its arguments in the body and
      // returns 405 for anything else.
      const resp = await apiHelper.apiCall(
        '/arcee/application/table/details/',
        {
          method: 'POST',
          body: JSON.stringify({ application_id: appId, table_id: table.id }),
          signal,
        },
      );
      if (!resp.ok) return { fields: [] };
      return resp.json();
    },
    enabled: !!table?.id,
    staleTime: 60_000,
  });

  const columns = useMemo(() => (details?.fields || []), [details]);

  const columnForField = useMemo(() => columnByField(op), [op]);

  const takenColumns = new Set(Object.values(columnForField).filter(Boolean));
  const serverColumns = Object.keys(op.server_fields || {});
  const ownerColumn = ownerColumnOf(op);

  const setFieldColumn = (fieldId, column) => {
    onChange(index, withFieldColumn(op, fieldId, column), columns);
  };

  const chooseTable = (name) => {
    // A different table means different columns; keeping the old mapping would
    // silently point at columns that are not there.
    onChange(index, { ...op, table: name, visitor_fields: {}, server_fields: {} }, columns);
  };

  const autoMap = () => onChange(index, autoMapped(op, formFields, columns), columns);

  const setServerField = (column, rule) => {
    onChange(index, { ...op, server_fields: { ...(op.server_fields || {}), [column]: rule } });
  };

  /**
   * Moves a server-filled value from one column to another, in ONE update.
   *
   * Doing it as a remove followed by a set does not work: both are computed
   * from the same `op`, so the second call carries the pre-removal state and
   * the old column survives alongside the new one.
   */
  const renameServerField = (from, to) => {
    if (!to || from === to) return;
    const next = {};
    Object.entries(op.server_fields || {}).forEach(([col, rule]) => {
      next[col === from ? to : col] = rule;
    });
    onChange(index, {
      ...op,
      server_fields: next,
      dedupe_on: (op.dedupe_on || []).map(c => (c === from ? to : c)),
    });
  };

  const removeServerField = (column) => {
    const next = { ...(op.server_fields || {}) };
    delete next[column];
    // A dedupe column must stay server-derived; the engine refuses a batch that
    // dedupes on anything a visitor controls. Dropping the column drops the rule
    // with it rather than leaving a configuration that cannot run.
    const dedupe = (op.dedupe_on || []).filter(c => c !== column);
    onChange(index, { ...op, server_fields: next, dedupe_on: dedupe });
  };

  /**
   * The answers this table is responsible for, and the ones the account step
   * already accounts for.
   *
   * Every field stays mappable either way — this only stops the panel calling
   * an answer "not saved anywhere" when it is in fact becoming the account.
   */
  const owned = accountFields || new Set();
  const savableFields = formFields.filter(f => !owned.has(f.id));
  const accountOwned = formFields.filter(f => owned.has(f.id));

  const mappedCount = savableFields
    .filter(f => columnForField[f.id]).length;

  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 8, padding: 12,
      background: '#fff', marginBottom: 10,
    }}>
      {/* Which table */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ flex: 1 }}>
          {/*
            Counted among the TABLE operations, not among every card. Tables do
            run in the order they are listed; an identity card sitting above one
            does not make it "second".
          */}
          <div style={labelStyle}>{tableStep === 0 ? 'Writes to' : 'Then writes to'}</div>
          <select style={inputStyle} value={op.table || ''}
            onChange={e => chooseTable(e.target.value)}>
            <option value="">— Choose a table —</option>
            {tables.map(t => (
              <option key={t.id} value={t.name}>{t.display_name || t.name}</option>
            ))}
          </select>
        </div>
        {total > 1 && (
          <button type="button" onClick={() => onRemove(index)} title="Remove this table"
            style={{
              border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626',
              borderRadius: 6, padding: '5px 8px', fontSize: 11, cursor: 'pointer',
            }}>
            <i className="bi bi-trash" />
          </button>
        )}
      </div>

      {!table && (
        <Empty icon="bi-table">Choose a table to map this form's answers into.</Empty>
      )}

      {table && (
        <>
          {/* Answers → columns */}
          <SectionLabel right={
            <button type="button" onClick={autoMap}
              style={{
                border: '1px solid #e2e8f0', background: '#f8fafc', color: theme.primary,
                borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>
              <i className="bi bi-magic me-1" />Match by name
            </button>
          }>
            ANSWERS · {mappedCount} of {savableFields.length} saved
          </SectionLabel>

          {savableFields.length > mappedCount && (
            <div style={{
              margin: '0 0 8px', padding: '7px 9px', background: '#fffbeb',
              borderRadius: 6, border: '1px solid #fde68a', fontSize: 10,
              color: '#92400e', lineHeight: 1.5,
            }}>
              <i className="bi bi-exclamation-triangle me-1" />
              {savableFields.length - mappedCount === 1
                ? '1 answer is not being saved anywhere.'
                : `${savableFields.length - mappedCount} answers are not being saved anywhere.`}
              {' '}A form that writes records keeps no separate copy, so anything
              without a column is discarded once it is submitted.
            </div>
          )}

          {savableFields.length === 0 ? (
            <Empty icon="bi-input-cursor-text">Add some fields first, on the Fields tab.</Empty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {savableFields.map(field => {
                const chosen = columnForField[field.id] || '';
                return (
                  <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: '42%', fontSize: 11, color: chosen ? '#374151' : '#b45309',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={field.label}>
                      {!chosen && (
                        <i className="bi bi-exclamation-triangle-fill me-1"
                          style={{ fontSize: 9, color: '#f59e0b' }} />
                      )}
                      {field.label || field.id}
                    </span>
                    <i className="bi bi-arrow-right" style={{ fontSize: 9, color: '#cbd5e1' }} />
                    <select
                      style={{ ...inputStyle, flex: 1, color: chosen ? '#0f172a' : '#94a3b8' }}
                      value={chosen}
                      onChange={e => setFieldColumn(field.id, e.target.value)}
                    >
                      <option value="">— not saved —</option>
                      {columns.map(c => (
                        <option key={c.name} value={c.name}
                          disabled={takenColumns.has(c.name) && c.name !== chosen}>
                          {c.display_name || c.name}
                          {serverColumns.includes(c.name) ? ' (server fills this)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {accountOwned.length > 0 && (
            <>
              <SectionLabel>USED BY THE ACCOUNT STEP</SectionLabel>
              <div style={{
                fontSize: 10, color: '#6b7280', lineHeight: 1.5, marginBottom: 6,
              }}>
                These answers are not discarded — they make the account, so this
                table does not need a column for them.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {accountOwned.map(field => {
                  const chosen = columnForField[field.id] || '';
                  return (
                    <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: '42%', fontSize: 11, color: '#6b7280',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={field.label}>
                        <i className="bi bi-person-fill me-1"
                          style={{ fontSize: 9, color: '#a78bfa' }} />
                        {field.label || field.id}
                      </span>
                      <i className="bi bi-arrow-right" style={{ fontSize: 9, color: '#cbd5e1' }} />
                      <select
                        style={{ ...inputStyle, flex: 1, color: chosen ? '#0f172a' : '#94a3b8' }}
                        value={chosen}
                        onChange={e => setFieldColumn(field.id, e.target.value)}
                      >
                        <option value="">— account only —</option>
                        {columns.map(c => (
                          <option key={c.name} value={c.name}
                            disabled={takenColumns.has(c.name) && c.name !== chosen}>
                            {c.display_name || c.name}
                            {serverColumns.includes(c.name) ? ' (server fills this)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Server-filled columns */}
          <SectionLabel>FILLED IN BY THE SERVER</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {serverColumns.map(column => {
              const rule = (op.server_fields || {})[column];
              const kind = describeRule(rule);
              return (
                <div key={column} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <select style={{ ...inputStyle, width: '42%' }} value={column}
                    onChange={e => renameServerField(column, e.target.value)}>
                    {columns.map(c => (
                      <option key={c.name} value={c.name}>{c.display_name || c.name}</option>
                    ))}
                  </select>
                  <i className="bi bi-arrow-left" style={{ fontSize: 9, color: '#cbd5e1' }} />
                  <select style={{ ...inputStyle, flex: 1 }} value={kind === 'ref' ? rule.from : kind}
                    onChange={e => {
                      const picked = e.target.value;
                      if (picked.startsWith('@')) setServerField(column, { from: picked });
                      else setServerField(column,
                        (SERVER_RULES.find(r => r.key === picked) || SERVER_RULES[0]).rule);
                    }}>
                    {SERVER_RULES.map(r => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                    {earlierRefs.map(r => (
                      <option key={r.ref} value={`@${r.ref}`}>
                        The {r.label} this form just created
                      </option>
                    ))}
                  </select>
                  {kind === 'const' && (
                    <input style={{ ...inputStyle, width: 90 }} value={rule.const ?? ''}
                      placeholder="value"
                      onChange={e => setServerField(column, { const: e.target.value })} />
                  )}
                  <button type="button" onClick={() => removeServerField(column)}
                    style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              );
            })}

            <button type="button"
              onClick={() => {
                const free = columns.find(c => !serverColumns.includes(c.name));
                if (free) setServerField(free.name, { from: 'user_id' });
              }}
              disabled={!columns.length}
              style={{
                border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#475569',
                borderRadius: 6, padding: '5px 0', fontSize: 11, cursor: 'pointer', width: '100%',
              }}>
              <i className="bi bi-plus-lg me-1" />Add a column the server fills
            </button>
          </div>

          {/* Many rows from one block of questions */}
          <SectionLabel>HOW MANY ROWS</SectionLabel>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!op.repeat}
              onChange={e => onChange(index, {
                ...op,
                repeat: e.target.checked,
                // Deduping identifies ONE row per person; with many rows per
                // person there is nothing single to identify, so the two rules
                // cannot both apply.
                dedupe_on: e.target.checked ? [] : (op.dedupe_on || []),
              })}
              style={{ marginTop: 2 }}
            />
            <span style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
              The person can add more of these
              <span style={{ display: 'block', color: '#94a3b8', fontSize: 10 }}>
                The answers above appear as a block with an <strong>Add another</strong>
                {' '}button, and every copy becomes one more row in this table — an event
                with three activities writes three rows. Later tables that point at this
                one follow each copy separately.
              </span>
            </span>
          </label>

          {/* One record per person — what makes an edit an edit */}
          {!op.repeat && (<>
          <SectionLabel>REPEAT SUBMISSIONS</SectionLabel>
          {ownerColumn ? (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={(op.dedupe_on || []).includes(ownerColumn)}
                onChange={e => onChange(index, {
                  ...op,
                  dedupe_on: e.target.checked ? [ownerColumn] : [],
                })}
                style={{ marginTop: 2 }}
              />
              <span style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
                One row per person
                <span style={{ display: 'block', color: '#94a3b8', fontSize: 10 }}>
                  Submitting again updates the row they already have, instead of adding
                  another. This is what lets somebody come back and edit what they sent —
                  and what stops a double-click registering twice.
                </span>
              </span>
            </label>
          ) : (
            <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.5 }}>
              Add a column filled with <strong>the signed-in person</strong> above to allow
              one row per person. Without it, every submission adds another row and nothing
              can be edited afterwards.
            </div>
          )}

          </>)}

          <div style={{
            marginTop: 10, padding: '7px 9px', background: '#f0f9ff',
            borderRadius: 6, border: '1px solid #bae6fd', fontSize: 10,
            color: '#0369a1', lineHeight: 1.5,
          }}>
            <i className="bi bi-shield-check me-1" />
            A value from the browser can only reach a column listed above. Columns the
            server fills are never sent by the browser, so they cannot be tampered with.
          </div>
        </>
      )}
    </div>
  );
}

// ─── The tab ─────────────────────────────────────────────────────────────────

export default function FormDataTab({ cfg, updateCfg, appId }) {
  const { theme } = useTheme();
  const operations = cfg.operations || [];
  const formFields = useMemo(() => allFormFields(cfg), [cfg]);

  const { data: tablesData, isLoading: loadingTables } = useQuery({
    queryKey: ['tableList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200`,
        { method: 'GET', signal },
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const tables = tablesData?.tables || tablesData?.results || [];

  /**
   * Each table operation's position among the TABLE operations, by its index in
   * the full list. Tables really do run in the order they are listed; identity
   * operations do not sit in that sequence at all, so they must not shift the
   * numbering of the cards that do.
   */
  const tableStepOf = useMemo(() => {
    const map = new Map();
    let step = 0;
    operations.forEach((op, i) => {
      if (!isIdentityOperation(op)) map.set(i, step++);
    });
    return map;
  }, [operations]);

  const hasIdentity = operations.some(isIdentityOperation);

  /**
   * The answers an identity operation consumes, so a table card stops
   * reporting them as data it is throwing away. They are not discarded — they
   * become the account.
   *
   * Membership is decided by the CONFIGURATION and nothing else: a field is in
   * this group because an identity operation points at it. What a field looks
   * like — a masked input, say — says nothing about where it may be stored, and
   * this panel does not decide storage policy.
   */
  const accountFields = useMemo(() => {
    const consumed = new Set();
    operations.filter(isIdentityOperation).forEach(op => {
      Object.values(op.fields || {}).forEach(source => {
        if (typeof source === 'string' && source.startsWith('@form.')) {
          consumed.add(source.slice('@form.'.length));
        }
      });
    });
    return consumed;
  }, [operations]);

  /**
   * The application's roles, read live rather than listed here. A role created
   * after this form was built appears in the picker on its own — the platform
   * keeps configuration as metadata, and a hard-coded list would go stale the
   * first time somebody adds one.
   */
  const { data: rolesData, isLoading: loadingRoles } = useQuery({
    queryKey: ['appRoles', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/user/roles/${appId}/?page_size=200`,
        { method: 'GET', signal },
      );
      if (!resp.ok) return { results: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const roles = rolesData?.results || rolesData?.roles || [];

  /**
   * The application's org groups, read live for the same reason the roles are.
   *
   * This picker is not decoration. A signed-in user holding NO group assignment
   * is widened to GLOBAL on a table whose visibility is 'public' — deliberately,
   * so logging in never shows fewer rows than browsing anonymously. The
   * consequence is that an account meant to see only its own records must be
   * given an assignment at signup; without one it reads every other account's
   * rows.
   */
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: ['appOrgGroups', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/groups/?application_id=${appId}`,
        { method: 'GET', signal },
      );
      if (!resp.ok) return [];
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const groups = Array.isArray(groupsData) ? groupsData : (groupsData?.results || []);

  /**
   * `columns` arrives from the card that changed, because that is the only
   * place the table's columns are loaded. It is optional: an identity
   * operation has no table, and a card whose columns are still loading passes
   * nothing — in both cases the rules are simply left alone.
   */
  const setOperation = (index, next, columns) => {
    updateCfg({
      operations: operations.map((op, i) => (i === index ? next : op)),
      ...fieldRulePatch(cfg, operations[index], next, columns),
    });
  };

  /**
   * An identity operation is APPENDED, not placed first.
   *
   * It still runs first — the server partitions identity operations out of the
   * list and executes them before any table write, so the account always exists
   * before a row points at it. Position in this array does not decide that.
   *
   * Position decides something else. The "edit my record" endpoint finds a
   * bound form's owner column by reading operations[0]; an identity operation
   * sitting there has no server_fields, so the form silently stops pre-filling
   * for everyone who comes back to it. Appending keeps the table operation
   * first and costs nothing.
   */
  const addIdentity = () => {
    const used = new Set(operations.map(op => op.ref));
    let n = 1;
    while (used.has(`u${n}`)) n += 1;
    updateCfg({
      operations: [
        ...operations,
        { kind: IDENTITY_KIND, mode: 'register', ref: `u${n}`, fields: {} },
      ],
    });
  };

  const addOperation = () => {
    const used = new Set(operations.map(op => op.ref));
    let n = operations.length + 1;
    while (used.has(`r${n}`)) n += 1;
    updateCfg({ operations: [...operations, { ref: `r${n}`, table: '', visitor_fields: {}, server_fields: {} }] });
  };

  const removeOperation = (index) => {
    updateCfg({ operations: operations.filter((_, i) => i !== index) });
  };

  return (
    <>
      {operations.length === 0 ? (
        <>
          <Empty icon="bi-database">
            This form only records submissions.<br />
            Connect it to a table to create real records.
          </Empty>
          <button type="button" onClick={addOperation} disabled={!appId}
            style={{
              width: '100%', border: 'none', background: theme.primary, color: '#fff',
              borderRadius: 7, padding: '8px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
            <i className="bi bi-link-45deg me-1" />Save answers to a table
          </button>
          <button type="button" onClick={addIdentity}
            style={{
              width: '100%', border: '1px dashed #ddd6fe', background: '#faf5ff',
              color: '#7e22ce', borderRadius: 7, padding: '7px 0', fontSize: 11,
              cursor: 'pointer', marginTop: 8,
            }}>
            <i className="bi bi-person-plus me-1" />Or sign somebody up
          </button>
        </>
      ) : (
        <>
          {loadingTables && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Loading tables…</div>
          )}

          {/*
            What actually happens on submit, spelled out.
            Without this the cards read as a sequence, and a form whose table
            card is listed above its account card looks like it writes rows for
            a person who does not exist yet. It does not: the server runs every
            identity operation ahead of every table write, whatever the order
            here. Saying so is cheaper than letting somebody rediscover it.
          */}
          {hasIdentity && (
            <div style={{
              marginBottom: 10, padding: '8px 10px', background: '#f8fafc',
              border: '1px solid #e2e8f0', borderRadius: 7,
              fontSize: 10, color: '#475569', lineHeight: 1.6,
            }}>
              <div style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '.04em',
                color: '#94a3b8', marginBottom: 4,
              }}>
                ON SUBMIT, IN THIS ORDER
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
                {['The account', 'Its role', 'Its group', 'The table rows', 'The email'].map((label, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <i className="bi bi-arrow-right" style={{ fontSize: 8, color: '#cbd5e1' }} />}
                    <span style={{
                      background: '#fff', border: '1px solid #e2e8f0',
                      borderRadius: 20, padding: '1px 8px', fontWeight: 600,
                    }}>{label}</span>
                  </React.Fragment>
                ))}
              </div>
              <div style={{ marginTop: 5 }}>
                The account is always created first, wherever its card sits below —
                a row could not name its owner otherwise. All of it is one
                transaction: if any part is refused, none of it is kept, and
                nobody is emailed about an account that was not created.
              </div>
            </div>
          )}

          {operations.map((op, index) => (isIdentityOperation(op) ? (
            <IdentityCard
              key={op.ref || `id-${index}`}
              op={op}
              index={index}
              total={operations.length}
              formFields={formFields}
              roles={roles}
              loadingRoles={loadingRoles}
              groups={groups}
              loadingGroups={loadingGroups}
              theme={theme}
              onChange={setOperation}
              onRemove={removeOperation}
            />
          ) : (
            <OperationCard
              key={op.ref || index}
              op={op}
              index={index}
              total={operations.length}
              tableStep={tableStepOf.get(index) ?? 0}
              tables={tables}
              accountFields={accountFields}
              formFields={formFields}
              appId={appId}
              theme={theme}
              // A table row can point at any row written BEFORE it — a child
              // cannot reference a parent that does not exist yet.
              //
              // An account is the exception, and it is not a special case so
              // much as the plain consequence of when things run: identity
              // operations execute ahead of every table write, so the account
              // is available to all of them however this list is ordered.
              // Offering it only to the cards listed below it hid a ref that
              // genuinely works.
              earlierRefs={operations
                .filter((e, i) => e.ref && (isIdentityOperation(e)
                  ? true
                  : (i < index && e.table)))
                .map(e => ({
                  ref: e.ref,
                  label: isIdentityOperation(e)
                    ? 'account'
                    : ((tables.find(t => t.name === e.table) || {}).display_name || e.table),
                }))}
              onChange={setOperation}
              onRemove={removeOperation}
            />
          )))}

          <button type="button" onClick={addIdentity}
            style={{
              width: '100%', border: '1px dashed #ddd6fe', background: '#faf5ff',
              color: '#7e22ce', borderRadius: 7, padding: '7px 0', fontSize: 11,
              cursor: 'pointer', marginBottom: 8,
            }}>
            <i className="bi bi-person-plus me-1" />Create an account as part of this form
          </button>

          <button type="button" onClick={addOperation}
            style={{
              width: '100%', border: '1px dashed #cbd5e1', background: '#f8fafc',
              color: '#475569', borderRadius: 7, padding: '7px 0', fontSize: 11,
              cursor: 'pointer', marginBottom: 12,
            }}>
            <i className="bi bi-plus-lg me-1" />Write to another table
          </button>

          <div style={{
            marginTop: 12, padding: '8px 10px', background: '#fffbeb',
            borderRadius: 6, border: '1px solid #fde68a', fontSize: 10,
            color: '#92400e', lineHeight: 1.5,
          }}>
            <i className="bi bi-person-check me-1" />
            A form that writes to a table asks the person to sign in first, so every row
            it creates has a real owner — and the rows are the record, so no separate
            copy of the answers is kept.
          </div>
        </>
      )}
    </>
  );
}
